/**
 * Filing auto-match (conservative)
 * --------------------------------
 * Pure helpers that scan a freshly-extracted document's text for a NAME that
 * matches one of the account's owner entities (user / family member / business)
 * and for a FOLDER name, so the Filing tab can PRE-FILL "Owner Of Document" and
 * "Select folder" as overridable suggestions.
 *
 * Deliberately conservative — it only returns a suggestion on an unambiguous,
 * whole-phrase match, otherwise null (leave the field blank for the user to pick).
 * This respects the standing rule that the owner is the uploader's choice: we only
 * SUGGEST an owner the document itself names, and the UI marks it "AI suggested".
 *
 * No I/O, no React — unit-tested in filingAutoMatch.test.ts.
 */

/** An owner entity reduced to the names worth matching (display name + nickname/acronym). */
export interface OwnerMatchCandidate {
  /** "owner_type:owner_id" — the value the Filing owner dropdown expects. */
  key: string;
  /** Every name/alias to try (display name, nickname, business acronym…). Blanks ignored. */
  names: string[];
  /** A person (not a business) — also match the name in "Last, First" order. */
  isPerson?: boolean;
}

/** A folder reduced to what we match on + who owns it (to disambiguate same-named folders). */
export interface FolderMatchCandidate {
  id: number;
  name: string;
  /** "owner_type:owner_id" of the folder's primary owner, if known. */
  ownerKey?: string;
}

// Names/folders shorter than this (normalized) are too collision-prone to match on.
const MIN_NAME_LEN = 3;
// A single-token name (no space) must be at least this long — avoids matching a
// 3-letter business token against an unrelated word in the body text.
const MIN_SINGLE_TOKEN_LEN = 4;

/** Lowercase, strip punctuation to single spaces, collapse runs — so matching is
 *  robust to commas, line breaks, and OCR spacing noise. */
export function normalizeText(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// True when `phraseNorm` appears in `haystackNorm` on token boundaries (both are
// space-normalized, so padding with spaces enforces whole-word/phrase matching).
function containsPhrase(haystackNorm: string, phraseNorm: string): boolean {
  if (!phraseNorm) return false;
  return ` ${haystackNorm} `.includes(` ${phraseNorm} `);
}

// Is this normalized name long/specific enough to risk matching on?
function isMatchableName(norm: string): boolean {
  if (norm.length < MIN_NAME_LEN) return false;
  const tokenCount = norm.split(' ').length;
  // Multi-token names (e.g. "john smith", "acme trading") are specific enough;
  // a lone token must clear the longer single-token bar.
  if (tokenCount < 2 && norm.length < MIN_SINGLE_TOKEN_LEN) return false;
  return true;
}

// A person's name as printed on mail is often "Last, First" (e.g. "Mehta, Sat" for
// "Sat Mehta"). Returns the name plus its token rotations so either order matches —
// rotations (not all permutations) keep multi-word first/last names intact.
function personNameOrders(norm: string): string[] {
  const tokens = norm.split(' ');
  if (tokens.length < 2 || tokens.length > 4) return [norm];
  return tokens.map((_, i) => [...tokens.slice(i), ...tokens.slice(0, i)].join(' '));
}

/**
 * Find the owner entity the document text names. Returns the entity key only when
 * EXACTLY ONE distinct entity matches (unambiguous); null otherwise — including when
 * several entities match (we won't guess between them).
 */
export function matchOwner(text: string, candidates: OwnerMatchCandidate[]): string | null {
  const hay = normalizeText(text);
  if (!hay) return null;

  const hitKeys = new Set<string>();
  for (const cand of candidates) {
    for (const name of cand.names) {
      const norm = normalizeText(name);
      if (!isMatchableName(norm)) continue;
      const orders = cand.isPerson ? personNameOrders(norm) : [norm];
      if (orders.some(o => containsPhrase(hay, o))) {
        hitKeys.add(cand.key);
        break;
      }
    }
  }
  return hitKeys.size === 1 ? hitKeys.values().next().value! : null;
}

/**
 * Find the folder the document text names. If `preferOwnerKey` is given (e.g. the
 * owner we just matched), that owner's folders are tried first so a name shared
 * across owners resolves to the right one. Returns a folder id only on a single
 * unambiguous match; null otherwise.
 */
export function matchFolder(
  text: string,
  folders: FolderMatchCandidate[],
  preferOwnerKey?: string | null,
): number | null {
  const hay = normalizeText(text);
  if (!hay) return null;

  const scan = (list: FolderMatchCandidate[]): number[] => {
    const ids: number[] = [];
    for (const f of list) {
      const norm = normalizeText(f.name);
      if (!isMatchableName(norm)) continue;
      if (containsPhrase(hay, norm)) ids.push(f.id);
    }
    return ids;
  };

  // Prefer the matched owner's own folders to disambiguate same-named folders.
  if (preferOwnerKey) {
    const owned = scan(folders.filter(f => f.ownerKey === preferOwnerKey));
    if (owned.length === 1) return owned[0];
    if (owned.length > 1) return null; // ambiguous within the owner — don't guess
  }

  const all = scan(folders);
  return all.length === 1 ? all[0] : null;
}
